/**
 * LevNutri — Painel do Bot WhatsApp
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { botService, authService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Ico, emo } from '../ui/Ico';

const BRAND = '#D4A574';
const GREEN = '#46714f';
const RED   = '#c46a60';

const ESTADO_CFG = {
  inicio:          { label: 'Menu',        color: '#736b64' },
  aguarda_opcao:   { label: 'Aguardando',  color: '#c4933f' },
  agenda_nome:     { label: 'Agendando',   color: '#7189a6' },
  agenda_tipo:     { label: 'Agendando',   color: '#7189a6' },
  agenda_horario:  { label: 'Agendando',   color: '#7189a6' },
  paciente_duvida: { label: 'Dúvida GPT',  color: '#7189a6' },
  finalizado:      { label: 'Finalizado',  color: '#46714f' },
  fora_horario:    { label: 'Fora horário',color: '#a39b93' },
};

const DIAS_ABREV = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
function diasLabel(json) {
  let dias;
  try { dias = JSON.parse(json || '[1,2,3,4,5]'); } catch { dias = [1, 2, 3, 4, 5]; }
  dias = (dias || []).filter(d => d >= 1 && d <= 7).sort((a, b) => a - b);
  if (dias.length === 0) return 'dias úteis';
  // Sequência contígua → "Seg a Sex"
  const contigua = dias.every((d, i) => i === 0 || d === dias[i - 1] + 1);
  if (contigua && dias.length >= 3) return `${DIAS_ABREV[dias[0]]} a ${DIAS_ABREV[dias[dias.length - 1]]}`;
  const nomes = dias.map(d => DIAS_ABREV[d]);
  return nomes.length > 1 ? nomes.slice(0, -1).join(', ') + ' e ' + nomes[nomes.length - 1] : nomes[0];
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  const now = new Date();
  const diff = (now - dt) / 1000;
  if (diff < 60)   return 'agora';
  if (diff < 3600) return Math.floor(diff/60) + 'min atrás';
  if (diff < 86400) return Math.floor(diff/3600) + 'h atrás';
  return dt.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
}

// ─── Conversa Modal ───────────────────────────────────────────────────────────

// ── Painel de saúde do canal (somente leitura) ────────────────────────────────
const SESSAO_CFG = {
  open:         { label: 'Sessão aberta',   cor: '#2f5236', bg: '#edf4ee', dot: GREEN },
  close:        { label: 'Sessão caída',    cor: '#a8483f', bg: '#f8ebe9', dot: RED },
  connecting:   { label: 'Conectando…',     cor: '#8a5f1a', bg: '#f8f1e2', dot: '#c4933f' },
  desconhecido: { label: 'Canal sem resposta', cor: '#4a4440', bg: '#f6f0ea', dot: '#a39b93' },
};
const STATUS_MSG_COR = {
  ERROR: { bg: '#f8ebe9', cor: '#a8483f' },
  DELIVERY_ACK: { bg: '#edf4ee', cor: '#2f5236' },
  READ: { bg: '#edf1f6', cor: '#4f6a86' },
  SERVER_ACK: { bg: '#f6f0ea', cor: '#4a4440' },
};

function HealthPanel() {
  const [h, setH] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    const load = async () => {
      try {
        const res = await botService.getHealth();
        if (vivo && res.success) { setH(res.data); setErro(false); }
      } catch { if (vivo) setErro(true); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  if (erro) return <div style={{ padding:'2rem', textAlign:'center', color:'#a8483f' }}>Não foi possível carregar a saúde do canal.</div>;
  if (!h)   return <div style={{ padding:'2rem', textAlign:'center', color:'#736b64' }}>Carregando…</div>;

  const s = SESSAO_CFG[h.sessao] || SESSAO_CFG.desconhecido;
  const card  = { background:'#ffffff', borderRadius:'16px', padding:'1.25rem 1.5rem', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #ebe3da' };
  const hCell = { fontSize:'0.7rem', color:'#a39b93', textTransform:'uppercase', textAlign:'left', padding:'0.3rem 0.5rem', borderBottom:'1px solid #f6f0ea' };
  const cell  = { fontSize:'0.8rem', color:'#4a4440', padding:'0.35rem 0.5rem', borderBottom:'1px solid #fbf8f5' };
  const chip  = (txt, bg, cor) => (
    <span style={{ fontSize:'0.75rem', fontWeight:600, background:bg, color:cor, padding:'0.25rem 0.7rem', borderRadius:'20px' }}>{emo(txt)}</span>
  );
  const fila = h.fila || {};

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
      {/* Estado geral */}
      <div style={{ ...card, display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
        <div style={{ width:14, height:14, borderRadius:'50%', background:s.dot, boxShadow:`0 0 0 4px ${s.dot}30` }} />
        <span style={{ fontWeight:700, color:s.cor }}>{emo(s.label)}</span>
        {emo(chip(h.canal === 'cloudapi' ? '☁️ CLOUD API OFICIAL' : '🔌 EVOLUTION', '#edf1f6', '#4f6a86'))}
        {chip(h.bot_ativo ? 'BOT ATIVO' : 'BOT MUTADO', h.bot_ativo ? '#edf4ee' : '#f8ebe9', h.bot_ativo ? '#2f5236' : '#a8483f')}
        {h.automacao_pausada && chip(`AUTOMAÇÃO PAUSADA até ${fmtDate(h.automacao_pausada_ate)}`, '#f8f1e2', '#8a5f1a')}
        <span style={{ marginLeft:'auto', fontSize:'0.72rem', color:'#a39b93' }}>atualiza a cada 15s</span>
      </div>

      {/* Fila + entregas 24h */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'1.25rem' }}>
        <div style={card}>
          <p style={{ margin:'0 0 0.75rem', fontWeight:600, color:'#5a3e2b', fontSize:'0.9rem' }}><Ico e="📥" sp />Fila de envio</p>
          <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap' }}>
            {[['Aguardando', fila.pending, '#8a5f1a'], ['Processando', fila.processing, '#4f6a86'],
              ['Falhas 24h', fila.failed_24h, '#a8483f'], ['Enviados 24h', fila.done_24h, '#2f5236']].map(([l, v, cor]) => (
              <div key={l}>
                <div style={{ fontSize:'1.4rem', fontWeight:700, color:cor }}>{v ?? 0}</div>
                <div style={{ fontSize:'0.72rem', color:'#736b64' }}>{l}</div>
              </div>
            ))}
          </div>
          {(h.proximos_jobs || []).length > 0 && (
            <p style={{ margin:'0.75rem 0 0', fontSize:'0.75rem', color:'#736b64' }}>
              Próximo job: #{h.proximos_jobs[0].id} ({h.proximos_jobs[0].type}) em {fmtDate(h.proximos_jobs[0].next_attempt_at)}
            </p>
          )}
        </div>
        <div style={card}>
          <p style={{ margin:'0 0 0.75rem', fontWeight:600, color:'#5a3e2b', fontSize:'0.9rem' }}><Ico e="📬" sp />Entregas (24h)</p>
          {Object.keys(h.entregas_24h || {}).length === 0
            ? <p style={{ margin:0, fontSize:'0.8rem', color:'#736b64' }}>Nenhum evento nas últimas 24h</p>
            : <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                {Object.entries(h.entregas_24h).map(([st, n]) => {
                  const c = STATUS_MSG_COR[st] || STATUS_MSG_COR.SERVER_ACK;
                  return chip(`${st}: ${n}`, c.bg, c.cor);
                })}
              </div>}
        </div>
      </div>

      {/* Últimos eventos de entrega */}
      <div style={card}>
        <p style={{ margin:'0 0 0.75rem', fontWeight:600, color:'#5a3e2b', fontSize:'0.9rem' }}><Ico e="🕐" sp />Últimos eventos de entrega</p>
        {(h.ultimos_eventos || []).length === 0
          ? <p style={{ margin:0, fontSize:'0.8rem', color:'#736b64' }}>Sem eventos registrados</p>
          : <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><th style={hCell}>Quando</th><th style={hCell}>Destinatário</th><th style={hCell}>Status</th></tr></thead>
                <tbody>
                  {h.ultimos_eventos.map((e, i) => {
                    const c = STATUS_MSG_COR[e.status] || STATUS_MSG_COR.SERVER_ACK;
                    return (
                      <tr key={i}>
                        <td style={cell}>{fmtDate(e.created_at)}</td>
                        <td style={cell}>{(e.remote_jid || '').replace('@s.whatsapp.net','').replace('@lid',' (lid)')}</td>
                        <td style={cell}>{chip(e.status, c.bg, c.cor)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>}
      </div>

      {/* Últimos envios rastreados (outbox) */}
      <div style={card}>
        <p style={{ margin:'0 0 0.75rem', fontWeight:600, color:'#5a3e2b', fontSize:'0.9rem' }}><Ico e="📤" sp />Últimos envios rastreados</p>
        {(h.ultimos_envios || []).length === 0
          ? <p style={{ margin:0, fontSize:'0.8rem', color:'#736b64' }}>Sem envios rastreados ainda</p>
          : <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><th style={hCell}>Quando</th><th style={hCell}>Para</th><th style={hCell}>Origem</th><th style={hCell}>Tipo</th><th style={hCell}>Situação</th></tr></thead>
                <tbody>
                  {h.ultimos_envios.map((e, i) => (
                    <tr key={i}>
                      <td style={cell}>{fmtDate(e.created_at)}</td>
                      <td style={cell}>{(e.jid || '').replace('@s.whatsapp.net','')}</td>
                      <td style={cell}>{e.origem}</td>
                      <td style={cell}>{e.type}</td>
                      <td style={cell}>{emo(e.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
      </div>
    </div>
  );
}

function ConversaModal({ conversa, onClose }) {
  const hist = conversa.historico || [];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'1rem' }}
      onClick={e => { if (e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:'#ffffff', borderRadius:'16px', width:'100%', maxWidth:'560px', maxHeight:'80vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #ebe3da', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:'0 0 0.1rem', fontWeight:600, fontSize:'1rem' }}>
              <Ico e="💬" /> {emo(conversa.nome || 'Contato desconhecido')}
            </h3>
            <p style={{ margin:0, fontSize:'0.75rem', color:'#736b64' }}>+{conversa.phone} · {fmtDate(conversa.ultima_mensagem)}</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.25rem', cursor:'pointer', color:'#736b64' }}><Ico e="✕" /></button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'1rem 1.25rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {hist.length === 0 ? (
            <p style={{ color:'#736b64', textAlign:'center', padding:'2rem' }}>Nenhuma mensagem registrada.</p>
          ) : hist.map((h, i) => (
            <div key={i} style={{
              alignSelf: h.origem === 'paciente' ? 'flex-start' : 'flex-end',
              maxWidth: '80%',
            }}>
              <div style={{
                background: h.origem === 'paciente' ? '#f6f0ea' : 'linear-gradient(135deg,#D4A574,#e8c9a8)',
                color: h.origem === 'paciente' ? '#2f2a26' : '#ffffff',
                padding: '0.5rem 0.75rem',
                borderRadius: h.origem === 'paciente' ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
              }}>
                {h.texto}
              </div>
              <p style={{ margin:'0.15rem 0 0', fontSize:'0.68rem', color:'#a39b93', textAlign: h.origem==='paciente'?'left':'right' }}>
                {emo(h.origem === 'paciente' ? '👤' : '🤖')} {h.ts ? fmtDate(new Date(h.ts*1000).toISOString()) : ''}
              </p>
            </div>
          ))}
        </div>
        <div style={{ padding:'0.75rem 1.25rem', borderTop:'1px solid #ebe3da' }}>
          <button onClick={onClose} style={{ width:'100%', padding:'0.65rem', background:'#f6f0ea', color:'#5a3e2b', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:600 }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Painel principal ─────────────────────────────────────────────────────────

export default function Bot() {
  const { showSuccess, showError } = useToast();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('status');
  const [conversas, setConversas]   = useState([]);
  const [viewing, setViewing]       = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [cfg, setCfg] = useState({
    ativo: '1',
    msg_boas_vindas: '',
    msg_fora_horario: '',
    msg_duvida_intro: '',
    pix_key: '',
    faq_clinica: '',
  });
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await botService.getStatus();
      if (res.success) {
        setData(res.data);
        if (res.data.config) {
          setCfg(prev => ({ ...prev, ...res.data.config }));
        }
      }
    } catch {}
    finally { setLoading(false); }
  }, []);

  const loadConversas = useCallback(async () => {
    try {
      const res = await botService.getConversations();
      if (res.success) setConversas(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadStatus();
    // Poll a cada 30s
    pollRef.current = setInterval(loadStatus, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadStatus]);

  useEffect(() => {
    if (tab === 'conversas') loadConversas();
  }, [tab, loadConversas]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await botService.saveConfig(cfg);
      if (res.success) showSuccess('Configurações salvas!');
      else showError(res.error || 'Erro ao salvar');
    } catch { showError('Erro ao salvar configurações'); }
    finally { setSavingConfig(false); }
  };

  const stats  = data?.stats  || {};
  const online = data?.online || false;

  const TABS = [
    { key: 'status',    label: '📊 Status' },
    { key: 'saude',     label: '🩺 Saúde do canal' },
    { key: 'conversas', label: '💬 Conversas' },
    { key: 'config',    label: '⚙️ Configurações' },
  ];

  return (
    <div style={{ maxWidth: 900 }}>
      {/* Modais */}
      {viewing && <ConversaModal conversa={viewing} onClose={() => setViewing(null)} />}

      {/* Header */}
      <div style={{ marginBottom:'1.5rem' }}>
        <h1 style={{ margin:'0 0 0.25rem', fontSize:'1.5rem', fontWeight:700, color:'#2f2a26' }}><Ico e="🤖" sp />Bot WhatsApp</h1>
        <p style={{ margin:0, fontSize:'0.875rem', color:'#736b64' }}>Secretária virtual{(() => { const u = authService.getCurrentUser(); const n = (u?.name_display || u?.name || '').trim(); return n ? ` — ${n}` : ' da nutricionista'; })()}</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1.5rem', borderBottom:'2px solid #ebe3da', paddingBottom:'0' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'0.6rem 1.1rem', background:'none', border:'none', borderBottom: tab===t.key ? `3px solid ${BRAND}` : '3px solid transparent',
              cursor:'pointer', fontWeight: tab===t.key ? 700 : 500, color: tab===t.key ? '#5a3e2b' : '#736b64',
              fontSize:'0.88rem', marginBottom:'-2px', transition:'all 0.15s' }}>
            {emo(t.label)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'#736b64' }}>Carregando...</div>
      ) : (
        <>
          {/* ── ABA SAÚDE DO CANAL ─────────────────────────────────────────────── */}
          {tab === 'saude' && <HealthPanel />}

          {/* ── ABA STATUS ─────────────────────────────────────────────────────── */}
          {tab === 'status' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

              {/* Card status principal */}
              <div style={{ background:'#ffffff', borderRadius:'16px', padding:'1.5rem', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #ebe3da' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
                  {/* Indicador online */}
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                    <div style={{
                      width:14, height:14, borderRadius:'50%',
                      background: online ? GREEN : RED,
                      boxShadow: online ? `0 0 0 4px ${GREEN}30` : `0 0 0 4px ${RED}20`,
                    }} />
                    <span style={{ fontWeight:600, fontSize:'1rem', color: online ? '#2f5236' : '#a8483f' }}>
                      {online ? 'Conectado' : 'Desconectado'}
                    </span>
                  </div>

                  {data?.phone && (
                    <span style={{ fontSize:'0.85rem', color:'#4a4440', background:'#fbf8f5', padding:'0.25rem 0.75rem', borderRadius:'20px' }}>
                      <Ico e="📱" sp />+{data.phone}
                    </span>
                  )}

                  {cfg.ativo === '1' ? (
                    <span style={{ fontSize:'0.78rem', fontWeight:600, background:'#edf4ee', color:'#2f5236', padding:'0.2rem 0.65rem', borderRadius:'20px' }}>BOT ATIVO</span>
                  ) : (
                    <span style={{ fontSize:'0.78rem', fontWeight:600, background:'#f8ebe9', color:'#a8483f', padding:'0.2rem 0.65rem', borderRadius:'20px' }}>BOT INATIVO</span>
                  )}

                  <span style={{ marginLeft:'auto', fontSize:'0.72rem', fontWeight:600, background:'#edf1f6', color:'#4f6a86', padding:'0.2rem 0.65rem', borderRadius:'20px' }}>
                    <Ico e="☁" sp />WhatsApp Cloud API (Meta)
                  </span>
                </div>

                {!online && (
                  <div style={{ marginTop:'1rem', background:'#f8f1e2', border:'1px solid #e3c48a', borderRadius:'10px', padding:'0.75rem 1rem', fontSize:'0.83rem', color:'#8a5f1a' }}>
                    <strong><Ico e="⚠" sp />Canal sem resposta.</strong> O número não retornou "conectado" agora há pouco. Se persistir, verifique o status do número no Gerenciador do WhatsApp (Meta Business) — não é mais necessário escanear QR Code.
                  </div>
                )}
              </div>

              {/* Stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:'1rem' }}>
                {emo([
                  { label:'Conversas iniciadas', value: stats.conversas ?? 0, icon:'💬', color:'#7189a6' },
                  { label:'Consultas agendadas', value: stats.agendamentos ?? 0, icon:'📅', color:'#46714f' },
                  { label:'Fichas enviadas',     value: stats.fichas ?? 0,      icon:'📋', color:'#7189a6' },
                  { label:'Taxa de conversão',   value: `${stats.taxa_conversao ?? 0}%`, icon:'📈', color:BRAND },
                ].map(s => (
                  <div key={s.label} style={{ background:'#ffffff', borderRadius:'12px', padding:'1.1rem', boxShadow:'0 2px 6px rgba(0,0,0,0.05)', border:'1px solid #ebe3da' }}>
                    <p style={{ margin:'0 0 0.25rem', fontSize:'1.6rem' }}>{emo(s.icon)}</p>
                    <p style={{ margin:'0 0 0.1rem', fontSize:'1.5rem', fontWeight:700, color:s.color }}>{s.value}</p>
                    <p style={{ margin:0, fontSize:'0.75rem', color:'#736b64' }}>{emo(s.label)}</p>
                  </div>
                )))}
              </div>

              {/* Conversas recentes */}
              <div style={{ background:'#ffffff', borderRadius:'16px', padding:'1.25rem', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #ebe3da' }}>
                <h3 style={{ margin:'0 0 1rem', fontSize:'0.95rem', fontWeight:600, color:'#2f2a26' }}><Ico e="💬" sp />Conversas recentes</h3>
                {(!data?.recentes || data.recentes.length === 0) ? (
                  <p style={{ color:'#736b64', textAlign:'center', padding:'1rem 0', fontSize:'0.85rem' }}>Nenhuma conversa iniciada ainda.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                    {data.recentes.map((c, i) => {
                      const ecfg = ESTADO_CFG[c.estado] || { label: c.estado, color:'#736b64' };
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.5rem 0.75rem', borderRadius:'10px', background:'#fbf8f5', cursor:'pointer' }}
                          onClick={() => { setViewing(c); setTab('conversas'); }}>
                          <div style={{ width:36, height:36, borderRadius:'50%', background:`${BRAND}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:600, color:BRAND, fontSize:'0.95rem', flexShrink:0 }}>
                            {(c.nome || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ margin:'0 0 0.05rem', fontWeight:600, fontSize:'0.87rem', color:'#2f2a26' }}>{emo(c.nome || c.phone)}</p>
                            <p style={{ margin:0, fontSize:'0.73rem', color:'#736b64' }}>+{c.phone}</p>
                          </div>
                          <span style={{ fontSize:'0.72rem', fontWeight:600, color:ecfg.color, background:`${ecfg.color}15`, padding:'0.15rem 0.55rem', borderRadius:'20px', flexShrink:0 }}>
                            {emo(ecfg.label)}
                          </span>
                          <span style={{ fontSize:'0.72rem', color:'#a39b93', flexShrink:0 }}>{fmtDate(c.ultima_mensagem)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Horário de atendimento (fonte única = Consultório) */}
              <div style={{ background:'#f6f0ea', borderRadius:'16px', padding:'1.25rem', border:'1px solid #d9d0c6' }}>
                <h3 style={{ margin:'0 0 0.5rem', fontSize:'0.95rem', fontWeight:600, color:'#5a3e2b' }}><Ico e="🕐" sp />Horário de atendimento do bot</h3>
                {data?.atendimento?.horario_inicio ? (
                  <p style={{ margin:'0 0 0.35rem', fontSize:'0.88rem', color:'#5a3e2b' }}>
                    O bot responde <strong>{diasLabel(data.atendimento.dias_atendimento)}</strong>, das{' '}
                    <strong>{data.atendimento.horario_inicio}</strong> às <strong>{data.atendimento.horario_fim}</strong>.
                  </p>
                ) : (
                  <p style={{ margin:'0 0 0.35rem', fontSize:'0.88rem', color:'#736b64' }}>Horário ainda não configurado.</p>
                )}
                <p style={{ margin:0, fontSize:'0.78rem', color:'#736b64' }}>
                  Para alterar, vá em <strong>Configurações → <Ico e="🏥" sp />Consultório</strong> — é a fonte única de horário (mesma dos horários de consulta).
                </p>
              </div>
            </div>
          )}

          {/* ── ABA CONVERSAS ──────────────────────────────────────────────────── */}
          {tab === 'conversas' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {conversas.length === 0 ? (
                <div style={{ textAlign:'center', padding:'3rem', color:'#d9d0c6', background:'#ffffff', borderRadius:'12px', border:'1px solid #ebe3da' }}>
                  <p style={{ fontSize:'2rem', margin:'0 0 0.5rem' }}><Ico e="💬" /></p>
                  <p style={{ margin:0 }}>Nenhuma conversa ainda.</p>
                </div>
              ) : conversas.map((c, i) => {
                const ecfg = ESTADO_CFG[c.estado] || { label:c.estado, color:'#736b64' };
                const hist = c.historico || [];
                const ultima = hist.length > 0 ? hist[hist.length-1] : null;
                return (
                  <div key={i}
                    onClick={() => setViewing(c)}
                    style={{ background:'#ffffff', borderRadius:'12px', padding:'1rem 1.25rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:'1px solid #ebe3da', display:'flex', alignItems:'center', gap:'1rem', cursor:'pointer', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#fbf8f5'}
                    onMouseLeave={e => e.currentTarget.style.background='#ffffff'}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background:`${BRAND}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:BRAND, fontSize:'1.1rem', flexShrink:0 }}>
                      {(c.nome||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ margin:'0 0 0.1rem', fontWeight:600, fontSize:'0.95rem', color:'#2f2a26' }}>{emo(c.nome || 'Desconhecido')}</p>
                      <p style={{ margin:'0 0 0.1rem', fontSize:'0.75rem', color:'#736b64' }}>+{c.phone}</p>
                      {ultima && (
                        <p style={{ margin:0, fontSize:'0.78rem', color:'#736b64', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {emo(ultima.origem==='paciente'?'👤':'🤖')} {ultima.texto}
                        </p>
                      )}
                    </div>
                    <div style={{ flexShrink:0, textAlign:'right' }}>
                      <span style={{ fontSize:'0.72rem', fontWeight:600, color:ecfg.color, background:`${ecfg.color}15`, padding:'0.15rem 0.55rem', borderRadius:'20px', display:'block', marginBottom:'0.3rem' }}>
                        {emo(ecfg.label)}
                      </span>
                      <span style={{ fontSize:'0.72rem', color:'#a39b93' }}>{fmtDate(c.ultima_mensagem)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── ABA CONFIGURAÇÕES ──────────────────────────────────────────────── */}
          {tab === 'config' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
              <div style={{ background:'#ffffff', borderRadius:'16px', padding:'1.5rem', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #ebe3da' }}>
                <h3 style={{ margin:'0 0 1.25rem', fontSize:'0.95rem', fontWeight:600, color:'#2f2a26' }}><Ico e="⚙" sp />Configurações Gerais</h3>

                {/* Toggle ativo */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 0', borderBottom:'1px solid #f6f0ea', marginBottom:'1rem' }}>
                  <div>
                    <p style={{ margin:'0 0 0.1rem', fontWeight:600, fontSize:'0.9rem' }}>Bot Ativo</p>
                    <p style={{ margin:0, fontSize:'0.78rem', color:'#736b64' }}>Desative para pausar o atendimento automático</p>
                  </div>
                  <button
                    onClick={() => setCfg(p => ({ ...p, ativo: p.ativo==='1'?'0':'1' }))}
                    style={{
                      width:52, height:28, borderRadius:14, border:'none', cursor:'pointer',
                      background: cfg.ativo==='1' ? GREEN : '#d9d0c6',
                      position:'relative', transition:'background 0.2s',
                    }}>
                    <div style={{
                      width:22, height:22, borderRadius:'50%', background:'#ffffff',
                      position:'absolute', top:3, transition:'left 0.2s',
                      left: cfg.ativo==='1' ? 27 : 3,
                      boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>
                </div>

                {/* Horário/dias moved to Consultório — aviso de fonte única */}
                <div style={{ marginBottom:'1rem', background:'#fbf8f5', border:'1px solid #d9d0c6', borderRadius:'10px', padding:'0.7rem 0.9rem', fontSize:'0.8rem', color:'#4a4440' }}>
                  <Ico e="🕐" /> <strong>Horário de atendimento</strong> agora é configurado em{' '}
                  <strong>Configurações → <Ico e="🏥" sp />Consultório</strong> (fonte única, mesma dos horários de consulta).
                </div>

                {/* Chave PIX */}
                <div style={{ marginBottom:'1rem' }}>
                  <label style={{ display:'block', fontSize:'0.78rem', fontWeight:600, color:'#5a3e2b', marginBottom:'0.3rem' }}>
                    <Ico e="🔑" sp />Chave PIX (enviada na confirmação de agendamento)
                  </label>
                  <input
                    type="text"
                    value={cfg.pix_key || ''}
                    onChange={e => setCfg(p => ({ ...p, pix_key: e.target.value }))}
                    placeholder="CPF, e-mail, telefone ou chave aleatória"
                    style={{ width:'100%', padding:'0.65rem 0.9rem', border:'1.5px solid #d9d0c6', borderRadius:'8px', fontSize:'0.9rem', outline:'none', boxSizing:'border-box' }}
                  />
                  <p style={{ margin:'0.2rem 0 0', fontSize:'0.72rem', color:'#736b64' }}>
                    Chave atual: <strong>{cfg.pix_key || '—'}</strong>
                  </p>
                </div>

                {/* FAQ da clínica — a assistente usa como fonte de verdade nas dúvidas */}
                <div style={{ marginBottom:'1rem' }}>
                  <label style={{ display:'block', fontSize:'0.78rem', fontWeight:600, color:'#5a3e2b', marginBottom:'0.3rem' }}>
                    <Ico e="🤖" sp />Informações para a assistente (FAQ)
                  </label>
                  <textarea
                    value={cfg.faq_clinica || ''}
                    onChange={e => setCfg(p => ({ ...p, faq_clinica: e.target.value }))}
                    rows={6}
                    placeholder={"Ex.: Valores das consultas, endereço, convênios aceitos, política de remarcação, dúvidas frequentes...\nA assistente virtual usa isto para responder as pacientes com precisão."}
                    style={{ width:'100%', padding:'0.65rem 0.9rem', border:'1.5px solid #d9d0c6', borderRadius:'8px', fontSize:'0.85rem', outline:'none', boxSizing:'border-box', resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
                  />
                  <p style={{ margin:'0.2rem 0 0', fontSize:'0.72rem', color:'#736b64' }}>
                    A assistente combina isto com o contexto de cada paciente (plano ativo, próxima consulta) para respostas personalizadas.
                  </p>
                </div>

                <div style={{ margin: '1rem 0', fontSize: '0.85rem', color: '#4a4440' }}>
                  As mensagens agora ficam em <a href="/app/configuracoes?aba=fluxo-bot">Configurações → Fluxo do Bot</a>, junto com a validação dos comprovantes PIX.
                </div>

                <button onClick={handleSaveConfig} disabled={savingConfig}
                  style={{ width:'100%', padding:'0.75rem', background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:600, fontSize:'0.95rem' }}>
                  {emo(savingConfig ? 'Salvando...' : '💾 Salvar Configurações')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
